import { useCallback, useEffect, useMemo } from "react";

import { useSelector } from "react-redux";

import type { AirCfarOffer, AirCfarOfferV2 } from "@b2bportal/air-cfar-api";
import {
  type FintechMarketplaceProductProps,
  type FintechMarketplaceProductOfferSelection,
  FintechMarketplaceProductViewType,
} from "@b2bportal/core-fintech-ui";
import { useCfarStyles, useModuleBEM } from "@b2bportal/core-themes";
import {
  CoreFlightsCfarOffersComponents,
  type SelectedCfarOfferStatus,
  SelectedStatus,
} from "@b2bportal/core-types";

import { useDeviceType } from "@b2bportal/core-utilities";
import { CfarOfferSection } from "@components/core-flights-cfar-offers";
import { useI18nContext } from "@hopper-b2b/i18n";
import { Button, ButtonTypeEnum, LazyLoading } from "@tenant/ui";
import clsx from "clsx";
import { useChooseCfarTracking } from "../../hooks";
import { FlightsCfarOffersSelectors } from "../../store";
import { getCurrencyFormatOptions } from "../../utils";

import defaultStyles from "./CfarFintechMarketplaceProduct.module.scss";

export interface CfarFintechMarketplaceProductProps
  extends FintechMarketplaceProductProps<AirCfarOffer | AirCfarOfferV2> {
  className?: string;
}

export const CfarFintechMarketplaceProduct = (
  props: CfarFintechMarketplaceProductProps
) => {
  const [block, cn] = useModuleBEM(
    useCfarStyles(
      CoreFlightsCfarOffersComponents.CfarFintechMarketplaceProduct,
      defaultStyles
    ),
    CoreFlightsCfarOffersComponents.CfarFintechMarketplaceProduct
  );
  const { t, formatFiatCurrency, brand } = useI18nContext();

  const termsAndConditionsLink =
    brand.urls.cfarTerms ?? brand.urls.termsAndConditions ?? "";

  const { isDesktopAndUp } = useDeviceType();
  const { className, onSelect, offers, productViewType } = props;

  const showCtaButton =
    !isDesktopAndUp ||
    productViewType === FintechMarketplaceProductViewType.INDIVIDUAL;

  const selectedCfarOffer = useSelector(
    FlightsCfarOffersSelectors.getSelectedCfarOffer
  );

  const cfarFareRefundability = useSelector(
    FlightsCfarOffersSelectors.getCfarFareRefundability
  );

  const cfarOfferTrackingProperties = useSelector(
    FlightsCfarOffersSelectors.getCfarOffersV2TrackingProperties
  );

  const trackChooseCfar = useChooseCfarTracking();

  const cfarOffers = useMemo(
    () => offers.map(({ payload }) => payload),
    [offers]
  );

  const currencyFormatOptions = useMemo(() => getCurrencyFormatOptions(), []);

  const ctaText = useMemo(() => {
    if (selectedCfarOffer.status === SelectedStatus.SELECTED) {
      const coveragePercentage =
        typeof selectedCfarOffer.offer.coveragePercentage === "string"
          ? selectedCfarOffer.offer.coveragePercentage
          : selectedCfarOffer.offer.coveragePercentage.value;
      return t("core-flights-cfar.cta.offerSelected", {
        coveragePercentage: `${coveragePercentage}%`,
        premiumAmount: formatFiatCurrency(
          selectedCfarOffer.offer.premiumAmount.fiat,
          currencyFormatOptions
        ),
      });
    }
    return t("core-flights-cfar.continue");
  }, [selectedCfarOffer, t, formatFiatCurrency, currencyFormatOptions]);

  const onCfarOfferSectionSelect = useCallback(
    (offer: SelectedCfarOfferStatus) => {
      if (isDesktopAndUp) {
        onSelect(
          toMarketplaceProductOffer(offer, t("core-flights-cfar.productName"))
        );
      }
    },
    [isDesktopAndUp, onSelect, t]
  );

  const onContinueClick = useCallback(() => {
    onSelect(
      toMarketplaceProductOffer(
        selectedCfarOffer,
        t("core-flights-cfar.productName")
      )
    );

    trackChooseCfar();
  }, [onSelect, selectedCfarOffer, t, trackChooseCfar]);

  useEffect(
    function autoCallOnSelectGivenPreselectedOfferOnDesktop() {
      if (!isDesktopAndUp) {
        return;
      }

      if (
        selectedCfarOffer.status === SelectedStatus.UNSELECTED ||
        selectedCfarOffer.status === SelectedStatus.UNAVAILABLE
      ) {
        return;
      }

      onSelect(
        toMarketplaceProductOffer(
          selectedCfarOffer,
          t("core-flights-cfar.productName")
        )
      );
    },
    [isDesktopAndUp, onSelect, selectedCfarOffer, t]
  );

  return (
    <div className={clsx(block, className)}>
      {cfarOffers.length > 0 ? (
        <div className={cn("container")}>
          <CfarOfferSection
            className={cn("cfar-offer-section")}
            cfarOffers={cfarOffers}
            termsAndConditionsLink={termsAndConditionsLink}
            selectedCfarOffer={selectedCfarOffer}
            cfarOfferTrackingProperties={
              cfarOfferTrackingProperties ? [cfarOfferTrackingProperties] : []
            }
            cfarFareRefundability={cfarFareRefundability}
            onSelect={onCfarOfferSectionSelect}
            showCloseButtonInDialog
          />

          {showCtaButton && (
            <div className={cn("continue-button-container")}>
              <Button
                className={cn("continue-button")}
                buttonType={ButtonTypeEnum.Primary}
                disabled={
                  selectedCfarOffer.status === SelectedStatus.UNSELECTED
                }
                onClick={onContinueClick}
              >
                {ctaText}
              </Button>
            </div>
          )}
        </div>
      ) : (
        <LazyLoading />
      )}
    </div>
  );
};

function toMarketplaceProductOffer(
  cfarOffer: SelectedCfarOfferStatus,
  offerTitle: string
): FintechMarketplaceProductOfferSelection {
  switch (cfarOffer.status) {
    case SelectedStatus.SELECTED:
      return {
        status: cfarOffer.status,
        offer: {
          premiumAmount: cfarOffer.offer.premiumAmount,
          title: offerTitle,
          payload: cfarOffer.offer,
        },
      };
    default:
      return {
        status: cfarOffer.status,
      };
  }
}
